/**
 * SettingsPage
 *
 * Full-page Settings surface (replaces the old SettingsModal popup). Uses the
 * standardized <SubPageShell> / <SubTabBar>, with the former modal tabs plus
 * Installations and Help as sibling sub-tabs:
 *
 *   General · API Keys · Local-First · Health · Billing · Applications · Installations · Help
 *
 * Environment gating mirrors the old modal:
 *   - api-keys / local-first / health / installations : local/desktop only
 *   - billing                                          : cloud OR Electron (not bare local Flask)
 *   - general / help                                   : always
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Settings as SettingsIcon,
  Key,
  Server,
  Activity,
  Receipt,
  Blocks,
  PackagePlus,
  HelpCircle,
} from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import { SubPageShell, type SubTabItem } from '../../components/common';
import { isLocalMode as checkIsLocalMode, isElectronMode } from '../../utils/apiMode';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { GeneralTab } from '../../components/settings/GeneralTab';
import { ApiKeySettings } from '../../components/settings/ApiKeySettings';
import { LocalFirstSettings } from '../../components/settings/LocalFirstSettings';
import { ComponentHealthStatus } from '../../components/settings/ComponentHealthStatus';
import { BillingHistory } from '../../components/settings/BillingHistory';
import { ConnectedApplications } from '../../components/settings/ConnectedApplications';
import { SettingsHelpTab } from '../../components/settings/SettingsHelpTab';
import { AdditionalInstallationsPage } from '../AdditionalInstallationsPage';

export type SettingsTabId =
  | 'general'
  | 'api-keys'
  | 'local-first'
  | 'health'
  | 'billing'
  | 'applications'
  | 'installations'
  | 'help';

interface SettingsPageProps {
  /** Sub-tab to open initially (e.g. 'billing' from the token popover). */
  initialSubTab?: string;
}

export function SettingsPage({ initialSubTab }: SettingsPageProps) {
  const { t } = useTranslation();
  const isLocalMode = checkIsLocalMode();
  // Billing is centralized in the cloud: available in serverless mode AND in the
  // Electron desktop app. Only hidden for a bare local Flask dev environment.
  const showBilling = !isLocalMode || isElectronMode();

  const tabs: SubTabItem[] = useMemo(
    () => [
      {
        id: 'general',
        label: t('settings.tabs.general', { defaultValue: 'General' }),
        icon: SettingsIcon,
      },
      {
        id: 'api-keys',
        label: t('settings.tabs.apiKeys', { defaultValue: 'API Keys' }),
        icon: Key,
        hidden: !isLocalMode,
      },
      {
        id: 'local-first',
        label: t('settings.tabs.localFirst', { defaultValue: 'Local-First' }),
        icon: Server,
        hidden: !isLocalMode,
      },
      {
        id: 'health',
        label: t('settings.tabs.health', { defaultValue: 'Health' }),
        icon: Activity,
        hidden: !isLocalMode,
      },
      {
        id: 'billing',
        label: t('settings.tabs.billing', { defaultValue: 'Billing' }),
        icon: Receipt,
        hidden: !showBilling,
      },
      {
        // Application grants (integrationMode Stage 06). Gated with billing
        // rather than with the local-only tabs: a grant is minted by Cloud Run
        // and bills the account, so the list is meaningless on a bare local
        // dev backend that has no account behind it.
        id: 'applications',
        label: t('settings.tabs.applications', { defaultValue: 'Applications' }),
        icon: Blocks,
        hidden: !showBilling,
      },
      {
        id: 'installations',
        label: t('settings.tabs.installations', { defaultValue: 'Installations' }),
        icon: PackagePlus,
        hidden: !isLocalMode,
      },
      {
        id: 'help',
        label: t('settings.tabs.help', { defaultValue: 'Help' }),
        icon: HelpCircle,
      },
    ],
    [t, isLocalMode, showBilling]
  );

  const visibleIds = useMemo(() => tabs.filter((tb) => !tb.hidden).map((tb) => tb.id), [tabs]);

  const isValid = (id?: string): id is SettingsTabId => id != null && visibleIds.includes(id);

  const [active, setActive] = useState<string>(() =>
    isValid(initialSubTab) ? initialSubTab : 'general'
  );

  // React to deep-links (e.g. token popover → billing).
  useEffect(() => {
    if (isValid(initialSubTab)) setActive(initialSubTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSubTab]);

  // If the active tab becomes hidden (mode change), fall back to general.
  useEffect(() => {
    if (!visibleIds.includes(active)) setActive('general');
  }, [visibleIds, active]);

  const renderBody = () => {
    switch (active) {
      case 'general':
        return (
          <Wrap>
            <GeneralTab />
          </Wrap>
        );
      case 'api-keys':
        return (
          <Wrap>
            <ApiKeySettings />
          </Wrap>
        );
      case 'local-first':
        return (
          <Wrap>
            <LocalFirstSettings />
          </Wrap>
        );
      case 'health':
        return (
          <Wrap>
            <ComponentHealthStatus />
          </Wrap>
        );
      case 'billing':
        return (
          <Wrap>
            <BillingHistory />
          </Wrap>
        );
      case 'applications':
        return (
          <Wrap>
            <ConnectedApplications />
          </Wrap>
        );
      case 'installations':
        return <AdditionalInstallationsPage />;
      case 'help':
        return (
          <Wrap>
            <SettingsHelpTab />
          </Wrap>
        );
      default:
        return (
          <Wrap>
            <GeneralTab />
          </Wrap>
        );
    }
  };

  return (
    <SubPageShell
      tabs={tabs}
      activeId={active}
      onChange={setActive}
      accent="purple"
      ariaLabel={t('settings.title', { defaultValue: 'Settings' })}
    >
      <ErrorBoundary section="settings" key={active}>
        {renderBody()}
      </ErrorBoundary>
    </SubPageShell>
  );
}

/** Consistent padded container for the form-style settings bodies. */
function Wrap({ children }: { children: React.ReactNode }) {
  return <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6">{children}</div>;
}
